import React from 'react';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { useClient } from '../../context/ClientContext';

export default function KPICard({ label, value, format = 'number', subtitle, previousValue, comparisonLabel, brandSplit, sparklineData }) {
  const { brand, dataSources } = useClient();
  const currencySymbol = dataSources?.googleAds?.currencySymbol || brand?.currencySymbol || '$';
  const primaryColor = brand.colors?.primary || '#2563eb';

  const formatVal = (v) => {
    if (v == null || v === undefined) return '—';
    switch (format) {
      case 'currency':
        return `${currencySymbol}${Number(v).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
      case 'percent':
        return `${Number(v).toFixed(1)}%`;
      case 'decimal':
        return Number(v).toFixed(2);
      default:
        return Number(v).toLocaleString('en-AU');
    }
  };

  // Calculate delta between current and previous
  const delta = (() => {
    if (previousValue == null || value == null) return null;
    if (previousValue === 0) return value > 0 ? 100 : 0;
    return ((value - previousValue) / previousValue) * 100;
  })();

  // For CPA, down is good. For everything else, up is good.
  const isCostMetric = /cost|cpa|spend/i.test(label);
  const isPositive = delta !== null && (isCostMetric ? delta < 0 : delta > 0);
  const isNegative = delta !== null && (isCostMetric ? delta > 0 : delta < 0);

  // Sparkline data points
  const sparkPoints = sparklineData?.length > 1
    ? sparklineData.map((v, i) => ({ v: v || 0, i }))
    : null;

  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 p-5">
      <div className="flex items-center gap-2 mb-2">
        <span
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: primaryColor }}
        />
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      </div>
      <div className="flex items-end gap-2">
        <p
          className="text-2xl font-bold text-gray-900"
          style={{ fontFamily: brand.typography?.headerFont }}
        >
          {formatVal(value)}
        </p>
        {delta !== null && delta !== 0 && (
          <span className={`text-xs font-medium mb-0.5 ${
            isPositive ? 'text-emerald-600' : isNegative ? 'text-red-500' : 'text-gray-400'
          }`}>
            {delta > 0 ? '↑' : '↓'}{Math.abs(delta).toFixed(0)}%
          </span>
        )}
      </div>
      {subtitle && (
        <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
      )}
      {delta !== null && (
        <p className="text-xs text-gray-400 mt-1">{comparisonLabel || 'vs last month'}</p>
      )}

      {/* Sparkline */}
      {sparkPoints && (
        <div className="mt-2 -mx-1">
          <ResponsiveContainer width="100%" height={32}>
            <LineChart data={sparkPoints}>
              <defs>
                <linearGradient id={`spark-${label}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={primaryColor} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={primaryColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Line
                type="monotone"
                dataKey="v"
                stroke={primaryColor}
                strokeWidth={1.5}
                strokeOpacity={0.5}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Brand vs Non-brand split */}
      {brandSplit && (
        <div className="flex gap-2 mt-2">
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white"
            style={{ backgroundColor: primaryColor }}
          >
            Brand: {formatVal(brandSplit.brand)}
          </span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-gray-600 bg-gray-100">
            Non-brand: {formatVal(brandSplit.nonBrand)}
          </span>
        </div>
      )}
    </div>
  );
}
